import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Badge, Button, Empty, ErrorBlock, Img, Input, Price, Spinner } from "../../components/ui.tsx";
import { ImageUpload } from "../../components/image-upload.tsx";
import { orgFetch } from "../../api/org-client.ts";
import { useCategories, useIdentities } from "../../api/queries.ts";
import {
  STATUS_LABEL,
  STATUS_TONE,
  type MyProduct,
  type ProductDetail,
} from "../my-products.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 我的商品
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **上架和改货是"电脑上的活"，这一页的网页版最该用力。**
 *    手机版是"列表 → 整页换成表单 → 返回"，因为屏幕一次只装得下一件事。
 *    网页上：列表是表格（状态/价/库存一眼横着比），编辑是
 *    **左表单 + 右状态操作栏**，改价、补库存、上下架不用来回滚。
 *
 * ⚠️ 上架的是**他自己的**商品（`supplierOrgId` 就是他），
 *    价格链从他这一级起算 —— 上级不在他自己的货上抽差价。
 */

/**
 * 新建商品。
 *
 * 🔴 **一步到位：商品信息 + 第一个规格一起填。**
 *    分成"先建商品、再加规格、再提交审核"三步的话很容易停在中间 ——
 *    建了个没有规格的商品，既卖不了也不知道差什么。
 */
function NewProduct({
  orgId,
  orgType,
  onDone,
  onCancel,
}: {
  orgId: number;
  /**
   * 🔴 **同一个字段，两种身份的含义完全不同。**
   *    - 供应商填的是**供货价**：下游各级要在它之上加自己的差价
   *    - 经销商/分销商填的是**他自己的售价**：货是他自己的，
   *      客户看到的就是这个数
   *    标错一个字，供应商会把零售价当供货价填进去，全链的价当场翻倍。
   */
  orgType: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const isSupplier = orgType === "supplier" || orgType === "platform";
  const { data: categories } = useCategories();
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [imageKeys, setImageKeys] = useState<string[]>([]);
  const [detailImageKeys, setDetailImageKeys] = useState<string[]>([]);
  const [spec, setSpec] = useState("标准装");
  /** ⚠️ 输入的是元，接口要分 —— 换算只做一次。 */
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [unit, setUnit] = useState("件");

  const create = useMutation({
    mutationFn: async () => {
      const product = await orgFetch<{ id: number }>("/products", orgId, {
        method: "POST",
        body: JSON.stringify({ title: title.trim(), categoryId, imageKeys, detailImageKeys }),
      });
      await orgFetch("/skus", orgId, {
        method: "POST",
        body: JSON.stringify({
          productId: product.id,
          specs: { 规格: spec.trim() || "标准装" },
          /**
           * 🔴 **他填的是"我卖多少"，也就是客户看到的价。**
           *    货是他自己的，没有"进货成本"这一说 —— 服务端会把这一级的
           *    成本和售价设成同一个数。
           */
          supplyPrice: Math.round(Number(price) * 100),
          stock: Number(stock || 0),
          unit: unit.trim() || "件",
        }),
      });
      /** ⚠️ 建完直接提交审核 —— 停在草稿里的商品谁也卖不了。 */
      await orgFetch(`/products/${product.id}/submit`, orgId, { method: "POST" });
      return product;
    },
    onSuccess: onDone,
  });

  const ready = title.trim().length >= 2 && categoryId && imageKeys.length > 0 && Number(price) > 0;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_300px] items-start gap-8">
      <div className="space-y-5">
        <section className="rounded-xl border border-border bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">商品信息</p>
          <div className="grid grid-cols-[minmax(0,1fr)_220px] gap-4">
            <Input
              label="商品名称"
              placeholder="例如：圣象强化复合地板 暖橡"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div>
              {/*
                ⚠️ 这里选的是**平台的全站品类**（瓷砖/地板/灯具），
                   不是店铺自己的分类 —— 前者决定客户在分类页能不能搜到它。
              */}
              <span className="mb-1.5 block text-[13px] text-muted-foreground">品类</span>
              <select
                className="h-11 w-full rounded-lg border border-border bg-card px-3 text-[15px] outline-none focus:border-brand-500"
                value={categoryId ?? ""}
                onChange={(e) => setCategoryId(Number(e.target.value) || null)}
              >
                <option value="">请选择</option>
                {(categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <span className="mb-1.5 block text-[13px] text-muted-foreground">
              商品图（至少一张）
            </span>
            <ImageUpload
              value={imageKeys}
              onChange={setImageKeys}
              scope="product"
              orgId={orgId}
              max={6}
            />
          </div>
        </section>


        <section className="rounded-xl border border-border bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">规格与价格</p>
          <div className="grid grid-cols-4 gap-4">
            <Input label="规格名" placeholder="标准装" value={spec} onChange={(e) => setSpec(e.target.value)} />
            <Input
              label={isSupplier ? "供货价（元）" : "我卖多少（元）"}
              inputMode="decimal"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <Input
              label="库存"
              inputMode="numeric"
              placeholder="0"
              value={stock}
              onChange={(e) => setStock(e.target.value)}
            />
            <Input
              label="单位"
              placeholder="件 / 片 / 平方米"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </div>
          {/*
            ⚠️ 一句话说清这个数是什么。供应商填错（把零售价当供货价）
               会让全链的价当场翻倍，而这个错要到有人下单才看得出来。
          */}
          <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
            {isSupplier
              ? "这是你给下游的价，各级会在它之上加自己的差价。对下游永远不可见。"
              : "这就是客户在你店里看到的价。"}
          </p>
        </section>

        {/*
          ── 商品详情图（详情页最下面那一长条）──

          🔴 **跟商品图是两回事**（淘宝那套）：商品图是"商品长什么样"
             （列表卡、详情页顶部轮播、购物车和订单缩略图都取它，得是方图）；
             详情图是"介绍商品"（材质说明、施工示意、尺寸表），
             通常是几张竖着排的长图，只出现在详情页最下面。
             合成一组的话，一张 800×3000 的说明图会缩成 80×80 的方块跑进列表。

          ⚠️ **摆在规格与价格后面**（老板指定）：填商品名、图、规格价格是
             "把货登记进来"，一口气填完；详情图是"怎么把货介绍出去"，
             是另一件事，而且它整块铺开占地方，夹在中间会把规格挤到屏幕外面。
        */}
        <section className="rounded-xl border border-border bg-card p-5">
          <p className="text-[15px] font-medium">商品详情图（选填）</p>
          <p className="mt-1 mb-4 text-[13px] text-muted-foreground">
            下面就是客户在详情页最下面看到的样子，从上往下排。
          </p>
          <ImageUpload
            value={detailImageKeys}
            onChange={setDetailImageKeys}
            scope="product"
            orgId={orgId}
            max={20}
            label="添加详情图"
            variant="strip"
          />
        </section>

        {create.error && <ErrorBlock error={create.error} />}
      </div>

      <aside className="sticky top-24 rounded-xl border border-border bg-card p-5">
        <p className="text-[15px] font-medium">提交</p>
        <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
          提交后由平台审核，通过就会出现在你店里。审核期间改不了 —— 要改得先撤回。
        </p>
        <Button
          className="mt-4 w-full"
          loading={create.isPending}
          disabled={!ready}
          onClick={() => create.mutate()}
        >
          提交审核
        </Button>
        <Button variant="ghost" className="mt-2 w-full" onClick={onCancel}>
          取消
        </Button>
      </aside>
    </div>
  );
}

/**
 * 改一件已经上架的商品。
 *
 * 🔴 **上架之后最常做的三件事：改价、补库存、下架。**
 * ⚠️ 库存用**相对增减**不是设值：两个人同时补货时"设成 100"会互相覆盖。
 */
function EditProduct({
  orgId,
  orgType,
  productId,
  categories,
}: {
  orgId: number;
  /** 见 `NewProduct` 的说明：同一个字段两种身份含义完全不同。 */
  orgType: string;
  productId: number;
  categories: { id: number; name: string }[];
}) {
  const isSupplier = orgType === "supplier" || orgType === "platform";
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["merchant", orgId, "product", productId],
    queryFn: () => orgFetch<ProductDetail>(`/products/${productId}`, orgId),
  });
  const myCat = useQuery({
    queryKey: ["merchant", orgId, "shop-categories"],
    queryFn: () => orgFetch<{ id: number; name: string }[]>("/shop/categories", orgId),
  });
  const [title, setTitle] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [detailImages, setDetailImages] = useState<string[]>([]);
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [addStock, setAddStock] = useState<Record<number, string>>({});
  const [shopCat, setShopCat] = useState<string>("");
  const [loaded, setLoaded] = useState(false);

  /** ⚠️ 数据回来之后灌一次初值 —— 只在 useState 里给的话输入框永远是空的。 */
  useEffect(() => {
    if (!detail.data || loaded) return;
    setTitle(detail.data.title);
    setImages(detail.data.imageKeys ?? []);
    setDetailImages(detail.data.detailImageKeys ?? []);
    setPrices(
      Object.fromEntries(
        detail.data.skus.map((k) => [k.id, String(+(k.supplyPrice / 100).toFixed(2))]),
      ),
    );
    setLoaded(true);
  }, [detail.data, loaded]);

  const saveInfo = useMutation({
    mutationFn: () =>
      orgFetch(`/products/${productId}`, orgId, {
        method: "PATCH",
        body: JSON.stringify({
          title: title.trim(),
          imageKeys: images,
          detailImageKeys: detailImages,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const savePrice = useMutation({
    mutationFn: async (v: { skuId: number; yuan: string }) => {
      const cents = Math.round(Number(v.yuan) * 100);
      await orgFetch(`/skus/${v.skuId}`, orgId, {
        method: "PATCH",
        body: JSON.stringify({ supplyPrice: cents }),
      });
      await orgFetch("/pricing/sku", orgId, {
        method: "POST",
        body: JSON.stringify({ skuId: v.skuId, price: cents }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const restock = useMutation({
    mutationFn: (v: { skuId: number; delta: number }) =>
      orgFetch(`/skus/${v.skuId}/stock`, orgId, {
        method: "POST",
        body: JSON.stringify({ delta: v.delta }),
      }),
    onSuccess: (_d, v) => {
      setAddStock((m) => ({ ...m, [v.skuId]: "" }));
      void qc.invalidateQueries({ queryKey: ["merchant", orgId] });
    },
  });

  /**
   * 🔴 **待审核的商品改不了**（审核员看到的必须就是最终上架的那份）——
   *    所以要给一条"撤回"的路。没有它的话，提交之后发现写错字就再也改不了了。
   */
  const withdraw = useMutation({
    mutationFn: () => orgFetch(`/products/${productId}/withdraw`, orgId, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const submit = useMutation({
    mutationFn: () => orgFetch(`/products/${productId}/submit`, orgId, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const toggle = useMutation({
    mutationFn: (online: boolean) =>
      orgFetch(`/products/${productId}/${online ? "online" : "offline"}`, orgId, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const setCategory = useMutation({
    mutationFn: (categoryId: number | null) =>
      orgFetch(`/shop/products/${productId}/category`, orgId, {
        method: "POST",
        body: JSON.stringify({ categoryId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  if (detail.isPending) return <Spinner compact />;
  if (detail.error) return <ErrorBlock error={detail.error} />;
  const d = detail.data!;

  const pending = d.status === "pending";

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_300px] items-start gap-8">
      {/* ══════════ 左：内容 ══════════ */}
      <div className="space-y-5">
        {d.status === "rejected" && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-5 py-4 text-[14px] leading-relaxed text-destructive">
            平台驳回了这件商品。改完之后重新提交审核。
          </div>
        )}

        <section className="rounded-xl border border-border bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">商品信息</p>
          <Input label="商品名称" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="mt-4">
            <span className="mb-1.5 block text-[13px] text-muted-foreground">商品图</span>
            <ImageUpload value={images} onChange={setImages} scope="product" orgId={orgId} max={6} />
          </div>
          {saveInfo.error && <ErrorBlock error={saveInfo.error} />}
          <Button
            className="mt-4"
            loading={saveInfo.isPending}
            disabled={title.trim().length < 2 || pending}
            onClick={() => saveInfo.mutate()}
          >
            {pending ? "审核中，改不了" : "保存"}
          </Button>
        </section>

        <section className="overflow-hidden rounded-xl border border-border bg-card">
          <p className="border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
            规格 · 价格 · 库存
          </p>
          {d.skus.map((k) => (
            <div key={k.id} className="border-b border-border px-5 py-4 last:border-b-0">
              <p className="mb-3 text-[15px]">{Object.values(k.specs).join(" / ") || "标准装"}</p>
              <div className="grid grid-cols-2 gap-6">
                {/*
                  🔴 **只有一个价：我卖多少。**
                     货是他自己的，"进货成本"这个概念对他不成立 ——
                     这个数就是客户在他店里看到的价，同时也是他下游进货的价。
                */}
                <div>
                  <div className="flex items-end gap-2">
                    <Input
                      label={isSupplier ? "供货价（元）" : "我卖多少（元）"}
                      inputMode="decimal"
                      className="flex-1"
                      value={prices[k.id] ?? ""}
                      onChange={(e) => setPrices((m) => ({ ...m, [k.id]: e.target.value }))}
                    />
                    <Button
                      loading={savePrice.isPending}
                      disabled={!Number(prices[k.id]) || pending}
                      onClick={() => savePrice.mutate({ skuId: k.id, yuan: prices[k.id] ?? "0" })}
                    >
                      改价
                    </Button>
                  </div>
                  <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                    {isSupplier
                      ? "这是你给下游的价，各级会在它之上加自己的差价。"
                      : "这就是客户在你店里看到的价。"}
                  </p>
                </div>

                <div>
                  <div className="flex items-end gap-2">
                    <Input
                      label={`补库存（当前 ${k.stock} ${k.unit}）`}
                      inputMode="numeric"
                      className="flex-1"
                      placeholder="填要增加的数量"
                      value={addStock[k.id] ?? ""}
                      onChange={(e) => setAddStock((m) => ({ ...m, [k.id]: e.target.value }))}
                    />
                    <Button
                      variant="outline"
                      loading={restock.isPending}
                      disabled={!Number(addStock[k.id])}
                      onClick={() => restock.mutate({ skuId: k.id, delta: Number(addStock[k.id]) })}
                    >
                      入库
                    </Button>
                  </div>
                  {/*
                    ⚠️ 说清楚是"增加"不是"设成" —— 写成设值的话两个人同时补货
                       会互相覆盖，而且少的那部分谁也发现不了。
                  */}
                  <p className="mt-1.5 text-[12px] text-muted-foreground">
                    填的是<strong>增加</strong>多少，不是改成多少。退库存填负数。
                  </p>
                </div>
              </div>
              {(savePrice.error || restock.error) && (
                <ErrorBlock error={savePrice.error ?? restock.error} />
              )}
            </div>
          ))}
        </section>

        {/*
          ── 商品详情图 ──
          🔴 **跟商品图是两回事**：商品图是"长什么样"（列表卡、订单缩略图都取它，
             得是方图）；详情图是"介绍商品"的长图，只排在详情页最下面。
          ⚠️ 摆在规格后面（老板指定），并且**整张铺开** —— 这一格就是详情页的预览。
        */}
        <section className="rounded-xl border border-border bg-card p-5">
          <p className="text-[15px] font-medium">商品详情图</p>
          <p className="mt-1 mb-4 text-[13px] text-muted-foreground">
            下面就是客户在详情页最下面看到的样子，从上往下排。
          </p>
          <ImageUpload
            value={detailImages}
            onChange={setDetailImages}
            scope="product"
            orgId={orgId}
            max={20}
            label="添加详情图"
            variant="strip"
          />
          {saveInfo.error && <ErrorBlock error={saveInfo.error} />}
          <Button
            className="mt-3"
            loading={saveInfo.isPending}
            disabled={title.trim().length < 2 || pending}
            onClick={() => saveInfo.mutate()}
          >
            {pending ? "审核中，改不了" : "保存详情图"}
          </Button>
        </section>
      </div>

      {/* ══════════ 右：状态与操作 ══════════ */}
      <aside className="sticky top-24 space-y-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <p className="text-[15px] font-medium">状态</p>
            <Badge variant={STATUS_TONE[d.status] ?? "secondary"}>
              {STATUS_LABEL[d.status] ?? d.status}
            </Badge>
          </div>

          {/*
            🔴 **状态决定这一页能干什么，要摆在最上面说清楚。**
               不说的话他改半天点保存，收到一句"商品正在审核中，不能修改"。
          */}
          {pending && (
            <>
              <p className="mt-2.5 text-[13px] leading-relaxed text-amber-800">
                正在等平台审核，这期间改不了 —— 审核员看到的必须就是最终上架的那份。
              </p>
              <Button
                variant="outline"
                className="mt-3 w-full"
                loading={withdraw.isPending}
                onClick={() => withdraw.mutate()}
              >
                撤回审核（回到草稿）
              </Button>
              {withdraw.error && <ErrorBlock error={withdraw.error} />}
            </>
          )}

          {/*
            🔴 下架之后**客户立刻看不到**，但已经下过的单不受影响 ——
               不说的话他不敢下架。
            ⚠️ 三种状态三个动作，别用一个按钮硬撑。
          */}
          {d.status === "active" && (
            <>
              <p className="mt-2.5 text-[13px] leading-relaxed text-muted-foreground">
                下架后客户立刻看不到。<strong>已经下过的单不受影响</strong>，该发的货照发。
              </p>
              <Button
                variant="outline"
                className="mt-3 w-full"
                loading={toggle.isPending}
                onClick={() => toggle.mutate(false)}
              >
                下架
              </Button>
            </>
          )}
          {d.status === "offline" && (
            <Button
              className="mt-3 w-full"
              loading={toggle.isPending}
              onClick={() => toggle.mutate(true)}
            >
              重新上架
            </Button>
          )}
          {(d.status === "draft" || d.status === "rejected") && (
            <Button className="mt-3 w-full" loading={submit.isPending} onClick={() => submit.mutate()}>
              提交审核
            </Button>
          )}
          {(toggle.error || submit.error) && <ErrorBlock error={toggle.error ?? submit.error} />}
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <p className="mb-2.5 text-[15px] font-medium">放进哪个货架</p>
          <select
            className="h-11 w-full rounded-lg border border-border bg-card px-3 text-[15px] outline-none focus:border-brand-500"
            value={shopCat}
            onChange={(e) => {
              setShopCat(e.target.value);
              setCategory.mutate(e.target.value ? Number(e.target.value) : null);
            }}
          >
            <option value="">未分类</option>
            {(myCat.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {/* ⚠️ 说明这是店铺货架不是全站品类，两个概念长得太像。 */}
          <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
            客户进你的店时按这个分组看商品。跟平台品类（
            {categories.map((c) => c.name).slice(0, 3).join(" / ")}…）是两回事。
          </p>
          {setCategory.error && <ErrorBlock error={setCategory.error} />}
        </div>
      </aside>
    </div>
  );
}

export function DesktopMyProducts() {
  const search = useSearch({ from: "/my-products" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;
  const myOrg = orgs.find((o) => o.id === orgId);
  const isSupplier = myOrg?.type === "supplier" || myOrg?.type === "platform";

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const { data: platformCats } = useCategories();

  const list = useQuery({
    queryKey: ["merchant", orgId, "my-products"],
    queryFn: () => orgFetch<{ items: MyProduct[] }>("/products?limit=50", orgId!),
    enabled: Boolean(orgId),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。** disabled 的查询 `isPending` 恒为 true ——
   *    未登录、或名下没有店的人会看到一个永远停不下来的圈。
   */
  if (!isPending && !orgId)
    return (
      <AccountShell title="我的商品">
        <Empty title="你还不是商家" hint="这是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} compact />
      </AccountShell>
    );
  if (isPending || list.isPending)
    return (
      <AccountShell title="我的商品">
        <Spinner compact />
      </AccountShell>
    );
  if (list.error)
    return (
      <AccountShell title="我的商品">
        <ErrorBlock error={list.error} />
      </AccountShell>
    );

  if (editingId && orgId)
    return (
      <AccountShell title="" crumb="编辑商品">
        <button
          className="mb-4 flex items-center gap-1.5 text-[14px] text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setEditingId(null)}
        >
          <ArrowLeft className="size-4" />
          返回商品列表
        </button>
        <EditProduct
          orgId={orgId}
          orgType={myOrg?.type ?? ""}
          productId={editingId}
          categories={platformCats ?? []}
        />
      </AccountShell>
    );

  if (creating && orgId)
    return (
      <AccountShell title="" crumb="上架商品">
        <button
          className="mb-4 flex items-center gap-1.5 text-[14px] text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setCreating(false)}
        >
          <ArrowLeft className="size-4" />
          返回商品列表
        </button>
        <NewProduct
          orgId={orgId}
          orgType={myOrg?.type ?? ""}
          onCancel={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ["merchant", orgId, "my-products"] });
          }}
        />
      </AccountShell>
    );

  const items = list.data?.items ?? [];

  return (
    <AccountShell title="我的商品">
      <Button className="mb-5" onClick={() => setCreating(true)}>
        <Plus className="size-4" />
        上架我自己的商品
      </Button>

      {items.length === 0 ? (
        <Empty
          title="还没有自己的商品"
          hint="自己进的辅料、自己做的定制柜都可以传上来卖，价格从你这一级起算"
          compact
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="grid grid-cols-[64px_minmax(0,1fr)_110px_110px_130px_110px_90px] items-center gap-4 bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
            <span />
            <span>商品</span>
            <span>状态</span>
            <span>规格数</span>
            <span className="text-right">{isSupplier ? "供货价" : "售价"}</span>
            <span className="text-right">库存</span>
            <span className="text-right">操作</span>
          </div>

          {items.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-[64px_minmax(0,1fr)_110px_110px_130px_110px_90px] items-center gap-4 border-t border-border px-5 py-3"
            >
              <Img
                src={p.imageUrls?.[0]}
                alt={p.title}
                className="size-14 shrink-0 rounded-lg object-cover"
              />
              <span className="min-w-0 truncate text-[14px]">{p.title}</span>
              <span>
                <Badge variant={STATUS_TONE[p.status] ?? "secondary"}>
                  {STATUS_LABEL[p.status] ?? p.status}
                </Badge>
              </span>
              <span className="text-[13px] text-muted-foreground">
                {p.skuCount != null ? `${p.skuCount} 个` : "—"}
              </span>
              <span className="text-right text-[14px]">
                {p.cost != null ? <Price cents={p.cost} /> : "—"}
              </span>
              <span className="text-right text-[14px] text-muted-foreground tabular-nums">
                {p.stock ?? "—"}
              </span>
              <span className="text-right">
                <button
                  className="text-[13px] text-brand-600 transition-colors hover:text-brand-700"
                  onClick={() => setEditingId(p.id)}
                >
                  编辑
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
